import { t } from '../../i18n';
import type { GameState, MapDefinition, TilePosition } from '../../model/gamestate/GameState';
import type { TileMapRenderer } from '../../renderer/tilemap/TileMapRenderer';
import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import { loadClientMapDefinition } from '../../terrain/MapLoader';
import type { StatusBarHandle } from '../../ui/StatusBar';
import type { PauseController } from '../../controller/PauseController';

interface RefreshNpcsParams {
  client: SpaceTimeClient;
  gameState: GameState;
  mapId: string;
  force?: boolean;
}

interface UnloadActiveMapParams {
  stopMapSubscriptions: () => void;
  gameState: GameState;
}

interface LoadActiveMapParams {
  client: SpaceTimeClient;
  config: { playerId: string; instanceId?: string };
  mapCache: Map<string, MapDefinition>;
  gameState: GameState;
  renderer: TileMapRenderer;
  statusBar: StatusBarHandle | null;
  hud: HTMLSpanElement | null;
  getInventoryCapacity: () => number;
  setInventoryCapacity: (next: number) => void;
  startMapSubscriptions: () => Promise<void>;
  onAfterMapLoaded?: (map: MapDefinition) => void;
}

interface EnterDungeonParams {
  pauseController: PauseController | null;
  client: SpaceTimeClient;
  unloadActiveMap: () => void;
  loadActiveMap: () => Promise<void>;
}

export async function refreshNpcs(params: RefreshNpcsParams): Promise<void> {
  const { client, gameState, mapId, force = false } = params;
  try {
    const npcs = await client.maps.fetchNpcsForMap(mapId, { forceRefresh: force });
    gameState.updateNpcs(npcs);
  } catch (error) {
    console.error('Failed to refresh npcs', error);
  }
}

export function unloadActiveMap(params: UnloadActiveMapParams): void {
  params.stopMapSubscriptions();
  params.gameState.clearMapState();

}

export async function loadActiveMap(params: LoadActiveMapParams): Promise<void> {
  const {
    client,
    config,
    mapCache,
    gameState,
    renderer,
    statusBar,
    hud,
    getInventoryCapacity,
    setInventoryCapacity,
    startMapSubscriptions,
    onAfterMapLoaded,
  } = params;

  try {
    const player = await client.players.fetchPlayerSnapshot(config.playerId);
    if (!player) {
      throw new Error(`Player ${config.playerId} not found`);
    }

    const currentInventoryCapacity = getInventoryCapacity();
    setInventoryCapacity(player.inventoryCapacity || currentInventoryCapacity);

    const [map, npcs] = await Promise.all([
      loadClientMapDefinition(client, mapCache, player.currentMapId),
      client.maps.fetchNpcsForMap(player.currentMapId, { forceRefresh: true })
    ]);

    statusBar?.update({
      hp: player.hp,
      maxHp: player.maxHp,
      mana: player.mana,
      maxMana: player.maxMana,
      stamina: player.stamina,
      maxStamina: player.maxStamina,
    });

    const position: TilePosition = { x: player.mapX, y: player.mapY };

    gameState.updateServerQueue([]);
    gameState.clearPendingQueue();
    gameState.setMap(map, player.currentMapId);
    gameState.applyAuthoritativeState(position, player.rotation);

    if (gameState.getIsTransitioning()) {
      gameState.completeMapTransition();
      gameState.applyAuthoritativeState(position, player.rotation);
    }

    renderer.swapToNewMap(map, player.currentMapId);
    renderer.setCharacterPosition(position, { immediate: true, rotation: player.rotation });
    renderer.setPlayerDisplayName(player.displayName);
    onAfterMapLoaded?.(map);

    gameState.updateRemotePlayers([]);
    gameState.updateRuntimeMobs([]);
    gameState.updateNpcs(npcs);

    config.instanceId = player.currentInstanceId;
    if (hud) {
      hud.textContent = t('hud.mapStatus', { mapId: player.currentMapId, x: position.x, y: position.y });
    }

    await startMapSubscriptions();

  } catch (error) {
    if (hud) hud.textContent = t('hud.errorLoadingMap');
    throw error;
  }
}

export async function enterDungeonFromUserMenu(params: EnterDungeonParams, dungeonId: string): Promise<void> {
  params.pauseController?.setPaused(false);
  await params.client.dungeon.enterDungeon(dungeonId);
  params.unloadActiveMap();
  await params.loadActiveMap();
}
